import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setSelectedPreviewPage } from "../../store/slices/navigationSlice";
import ESMComponentWrapper from "../esm/ESMComponentWrapper";
import ProjectHeader from "./ProjectHeader";
import TaskInstructions from "./TaskInstructions";
import ImagesPreview from "./ImagesPreview";
import WorkerRequirements from "./WorkerRequirements";
import TaskDetails from "./TaskDetails";
import QualityControl from "./QualityControl";
import ProjectObjectivesPreview from "./ProjectObjectivesPreview";
import { Play } from "lucide-react";

function mapComponentToEsm(
  component,
  { metadata, images, settings, objectives } = {},
) {
  const type = component?.type;
  const content = component?.content || {};
  const esmName = type === "Splash Page" ? "ProjectCard" : "HeadlineOnly";

  const baseProps = {};
  switch (esmName) {
    case "HeadlineOnly":
      baseProps.title = content.headline || metadata?.name || "";
      break;
    case "TextOnlyFullPage":
      baseProps.content = content.body || metadata?.shortDescription || "";
      break;
    case "StandaloneButton":
      baseProps.buttonName = content.buttonLabel || "Apply";
      baseProps.ctaUrl = content.buttonUrl || "#";
      break;
    case "ImageOnly":
      baseProps.mediaUrl = content.mediaUrl || "";
      break;
    case "ProjectCard": {
      const imageUrl =
        (Array.isArray(images) && images[0]?.url) ||
        "https://placehold.co/600x360?text=Project+Image";
      const payRateNum =
        Number((settings && settings.paymentPerTask) || 0) || 0;
      baseProps.type = "project";
      baseProps.hoverable = true;
      baseProps.fixedWidth = 330;
      baseProps.project = {
        projectId: metadata?.shortName || "preview-project",
        projectObjectiveName:
          objectives?.objectiveName || metadata?.name || "Project",
        projectDescription: metadata?.shortDescription || "",
        imageUrl,
        payRate: payRateNum,
        rateType: "Hour",
        invitedToProjectDate: new Date().toISOString(),
      };
      baseProps.action = () => {};
      baseProps.titleSize = 20;
      baseProps.rateSize = 16;
      break;
    }
    case "TwoColumn5050":
      baseProps.leftTitle = content.headline || metadata?.name || "";
      baseProps.leftContent = content.body || metadata?.shortDescription || "";
      baseProps.leftButtonName = content.buttonLabel || undefined;
      baseProps.leftCtaUrl = content.buttonUrl || undefined;
      baseProps.rightTitle = content.headline || metadata?.name || "";
      baseProps.rightContent = content.body || metadata?.shortDescription || "";
      baseProps.rightButtonName = content.buttonLabel || undefined;
      baseProps.rightCtaUrl = content.buttonUrl || undefined;
      break;
    case "ThreeColumn33":
      baseProps.leftTitle = content.headline || metadata?.name || "";
      baseProps.leftContent = content.body || metadata?.shortDescription || "";
      baseProps.middleTitle = content.headline || "";
      baseProps.middleContent = content.body || "";
      baseProps.rightTitle = content.headline || "";
      baseProps.rightContent = content.body || "";
      break;
    case "TwoColumnImageLeft5050":
    case "TwoColumnImageRight5050":
    case "TwoColumnImageLeft3366":
    case "TwoColumnImageRight3366":
    case "CommonProjectPage":
      baseProps.title = content.headline || metadata?.name || "";
      baseProps.content = content.body || metadata?.shortDescription || "";
      break;
    default:
      baseProps.title = content.headline || metadata?.name || "";
      baseProps.content = content.body || metadata?.shortDescription || "";
      if (content.mediaUrl) baseProps.mediaUrl = content.mediaUrl;
      if (content.buttonLabel) baseProps.buttonName = content.buttonLabel;
      if (content.buttonUrl) baseProps.ctaUrl = content.buttonUrl;
      break;
  }

  return { esmName, props: baseProps };
}

const ProjectPreview = () => {
  const dispatch = useDispatch();

  // Select state from Redux
  const metadata =
    useSelector((state) => state.project.creation.metadata) || {};
  const settings =
    useSelector((state) => state.project.creation.settings) || {};
  const objectives =
    useSelector((state) => state.projectObjective.creation.objectives) || {};
  const qualifications =
    useSelector((state) => state.qualification.creation.qualifications) || [];
  const currentStep = useSelector((state) => state.navigation.currentStep);
  const selectedPreviewPageId = useSelector(
    (state) => state.navigation.selectedPreviewPageId,
  );

  const qualificationSteps =
    useSelector(
      (state) => state.qualificationStep.creation.qualificationSteps,
    ) || [];
  const pages = useSelector((state) => state.page.creation.pages) || [];
  const images = useSelector((state) => state.page.creation.images) || [];

  // Ensure a default selected page if none picked and pages exist
  useEffect(() => {
    if (!selectedPreviewPageId && pages?.length > 0) {
      dispatch(setSelectedPreviewPage(pages[0].id));
    }
  }, [selectedPreviewPageId, pages, dispatch]);

  const selectedPage = useMemo(
    () => pages?.find((p) => p.id === selectedPreviewPageId) || null,
    [pages, selectedPreviewPageId],
  );

  const mapComp = (component) =>
    mapComponentToEsm(component, { metadata, images, settings, objectives });

  return (
    <div className="slide-in-right">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Preview Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Live Preview</h3>
              <p className="text-sm text-gray-600">
                How your project will appear to workers
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              Live
            </div>
          </div>
        </div>

        {/* Preview Content */}
        <div className="p-6 space-y-6">
          {/* Project Header - always visible */}
          <ProjectHeader metadata={metadata} />

          {/* Step 1: Project Information - exemplar instructions */}
          {currentStep === 1 && <TaskInstructions />}

          {/* Images Preview */}
          {currentStep === 1 && <ImagesPreview images={images} />}

          {/* Step 5: Worker Requirements */}
          {currentStep === 5 && (
            <WorkerRequirements qualifications={qualifications} />
          )}

          {/* Step 6: Payment & Time Info */}
          {currentStep === 6 && <TaskDetails settings={settings} />}

          {/* Step 6: Quality Settings */}
          {currentStep === 6 && <QualityControl settings={settings} />}

          {/* Step 2: Project Objectives */}
          {currentStep === 2 && (
            <ProjectObjectivesPreview objectives={objectives} />
          )}

          {/* Step 3: Qualification Steps */}
          {currentStep === 3 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Qualification Steps
              </h3>
              {qualificationSteps?.length ? (
                <div className="space-y-2">
                  {qualificationSteps.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded border text-sm"
                    >
                      <div className="space-y-0.5">
                        <div className="font-medium text-gray-900">
                          {s.step}. {s.name}
                        </div>
                        <div className="text-gray-600">
                          Objective: {s.objective || "—"} • Status: {s.status} •
                          Funnel: {s.funnel} • Attempts: {s.attempts} • Qual
                          Step: {s.qualificationStep}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  No qualification steps added yet.
                </div>
              )}
            </div>
          )}

          {/* Step 4: Project Pages - Single-page ESM preview */}
          {currentStep === 4 && (
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  Project Pages Preview
                </h3>
                {pages?.length > 0 && (
                  <select
                    value={selectedPreviewPageId || ""}
                    onChange={(e) =>
                      dispatch(setSelectedPreviewPage(Number(e.target.value)))
                    }
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    {pages.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.type}
                        {p.title ? ` - ${p.title}` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {selectedPage ? (
                <div>
                  {selectedPage.components?.length ? (
                    selectedPage.components.map((comp) => {
                      const { esmName, props } = mapComp(comp);
                      return (
                        <div key={comp.id}>
                          <ESMComponentWrapper
                            componentName={esmName}
                            noWrapper={true}
                            {...props}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-sm text-gray-500">
                      No components added to this page yet.
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  Create a page to preview it here.
                </div>
              )}
            </div>
          )}

          {/* Start Button - visible on steps 1 and 6 for context */}
          {(currentStep === 1 || currentStep === 6) && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <button className="w-full flex items-center justify-center gap-2 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
                <Play className="w-4 h-4" />
                Start Working on This Project
              </button>
              <p className="text-xs text-center text-gray-600 mt-2">
                This is how the start button will appear to workers
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectPreview;
